/**
 * Communication Tunnel API
 * Handles anonymous communication management - aliases, conversations, messages
 */
import axios from 'axios';
import { withAccountLanguage } from '../utils/accountLanguage';

const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const BASE_URL = process.env.REACT_APP_COMMUNICATION_SERVICE_URL 
  || (isDevelopment 
    ? 'http://localhost:8097/api/v1'
    : 'https://erp.escooterlab.ch/actor-management/api/v1/communication-tunnel');

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Guest meeting credentials are the only authority for these public endpoints.
// Keep them on a client without the ERP staff-token interceptor so a logged-in
// browser cannot accidentally attach an employee identity to a guest request.
const guestApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  const suppliedAuthorization = config.headers?.Authorization || config.headers?.get?.('Authorization');
  if (token && !suppliedAuthorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============== Aliases ==============

export const createAlias = async (aliasData) => {
  const response = await api.post('/aliases/', aliasData);
  return response.data;
};

export const getAlias = async (aliasId) => {
  const response = await api.get(`/aliases/${aliasId}`);
  return response.data;
};

export const getAliasByCode = async (aliasCode) => {
  const response = await api.get(`/aliases/code/${aliasCode}`);
  return response.data;
};

export const getAliasByCustomer = async (customerId) => {
  const response = await api.get(`/aliases/customer/${customerId}`);
  return response.data;
};

export const lookupOrCreateAlias = async (aliasData) => {
  const response = await api.post('/aliases/lookup', aliasData);
  return response.data;
};

// ============== Conversations ==============

export const getUnifiedInbox = async (page = 1, pageSize = 20, status = null, channel = null, revealIdentity = false, attention = null, workmailOnly = false, excludeChannel = null, excludeWorkmail = false, groupsOnly = false, chatbotOnly = false, requestConfig = {}, filters = {}) => {
  const params = { page, page_size: pageSize };
  if (status) params.status = status;
  if (channel) params.channel = channel;
  if (revealIdentity) params.reveal_identity = true;
  if (attention) params.attention = attention;
  if (workmailOnly) params.workmail_only = true;
  if (excludeChannel) params.exclude_channel = excludeChannel;
  if (excludeWorkmail) params.exclude_workmail = true;
  if (groupsOnly) params.groups_only = true;
  if (chatbotOnly) params.chatbot_only = true;
  if (filters.direction) params.direction = filters.direction;
  if (filters.q?.trim()) params.q = filters.q.trim();
  if (filters.mailCategory) params.mail_category = filters.mailCategory;
  if (filters.excludeMailNoise) params.exclude_mail_noise = true;
  if (filters.owner) params.owner = filters.owner;

  const response = await api.get('/conversations/inbox', { ...requestConfig, params });
  return response.data;
};

// Retro-link existing conversations to their customer (actor) + open repair.
// New inbound messages auto-link; this fixes chats whose last activity was outbound.
export const backfillActorLinks = async (limit = 80) => {
  const response = await api.post('/conversations/backfill-actor-links', null, { params: { limit } });
  return response.data;
};

export const createConversation = async (conversationData) => {
  const response = await api.post('/conversations/', conversationData);
  return response.data;
};

export const getConversation = async (conversationId) => {
  const response = await api.get(`/conversations/${conversationId}`);
  return response.data;
};

export const getConversationMailPolicy = async (conversationId) => {
  const response = await api.get(`/mail-areas/conversations/${encodeURIComponent(conversationId)}`);
  return response.data;
};

export const getConversationsForProcess = async (processId) => {
  const response = await api.get(`/conversations/process/${processId}`);
  return response.data;
};

export const getConversationsForCustomer = async (customerId) => {
  const response = await api.get(`/conversations/customer/${customerId}/history`);
  return response.data;
};

export const getActorConversationHistory = async (actorId, page = 1, pageSize = 20, status = null) => {
  const params = { page, page_size: pageSize };
  if (status) params.status = status;
  const response = await api.get(`/conversations/customer/${actorId}/history`, { params });
  return response.data;
};

export const getActorCommunicationTimeline = async (actorId, limit = 200) => {
  const response = await api.get(`/conversations/customer/${encodeURIComponent(actorId)}/timeline`, {
    params: { limit: Math.min(500, Math.max(1, Number(limit) || 200)) },
  });
  return response.data;
};

export const getSalesMailbox = async (actorIds = [], limit = 500) => {
  const ids = [...new Set((Array.isArray(actorIds) ? actorIds : []).map(value => String(value || '').trim()).filter(Boolean))];
  const response = await api.post('/conversations/sales-mailbox', {
    actor_ids: ids.slice(0, 5000),
    limit: Math.min(1000, Math.max(1, Number(limit) || 500)),
  });
  return response.data;
};

export const getConversationMessageAnchor = async (conversationId, messageId) => (
  await api.get(`/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/anchor`)
).data;

export const getConversationMessages = async (conversationId, limit = 50, offset = 0, channel = null) => {
  const params = { limit, offset };
  if (channel) {
    params.channel = channel;
  }

  const response = await api.get(`/conversations/${conversationId}/messages`, {
    params
  });
  return response.data;
};

export const getTwilioWhatsAppTemplates = async () => {
  const response = await api.get('/twilio/whatsapp/templates');
  return response.data;
};

export const sendMessage = async (conversationId, messageData) => {
  const response = await api.post(`/conversations/${conversationId}/messages`, withAccountLanguage(messageData));
  return response.data;
};

export const updateMessageTriage = async (conversationId, messageId, triageData) => {
  const response = await api.patch(`/conversations/${conversationId}/messages/${messageId}/triage`, triageData);
  return response.data;
};

export const setMessageReaction = async (conversationId, messageId, reactionData) => {
  const response = await api.patch(`/conversations/${conversationId}/messages/${messageId}/reaction`, reactionData);
  return response.data;
};

export const updateConversationTriage = async (conversationId, triageData) => {
  const response = await api.patch(`/conversations/${conversationId}/triage`, triageData);
  return response.data;
};

// ============== Group Chats (anonymous, multi-channel) ==============

// participants: [{ alias_code?|real_email?|real_phone?|real_name?, role, channel, display_name? }]
export const createGroupChat = async ({ subject, participants, process_id = null, repair_id = null }) => {
  const response = await api.post('/conversations/groups', { subject, participants, process_id, repair_id });
  return response.data;
};

export const getGroupParticipants = async (conversationId, includeInactive = false) => {
  const response = await api.get(`/conversations/${conversationId}/participants`, {
    params: includeInactive ? { include_inactive: true } : {},
  });
  return response.data;
};

export const addGroupParticipant = async (conversationId, participant) => {
  const response = await api.post(`/conversations/${conversationId}/participants`, participant);
  return response.data;
};

export const removeGroupParticipant = async (conversationId, participantId) => {
  const response = await api.delete(`/conversations/${conversationId}/participants/${participantId}`);
  return response.data;
};

export const broadcastToGroup = async (conversationId, { content, subject = null, sender_display = 'Agent', sender_participant_id = null }) => {
  const response = await api.post(`/conversations/${conversationId}/broadcast`, {
    content, subject, sender_display, sender_participant_id,
  });
  return response.data;
};

// Central customer recognition: contact, chats, open and past repairs.
export const lookupCustomer = async ({ phone, email } = {}) => {
  const params = {};
  if (phone && phone.trim()) params.phone = phone.trim();
  if (email && email.trim()) params.email = email.trim();
  const response = await api.get('/identity/lookup', { params });
  return response.data;
};

export const mergeCustomerContacts = async (duplicateId, primaryId, reason) => {
  const response = await api.post('/identity/merge', { duplicate_id: duplicateId, primary_id: primaryId, reason });
  return response.data;
};

export const getConversationCustomer = async (conversationId) => {
  const response = await api.get(`/identity/conversations/${conversationId}`);
  return response.data;
};

export const relinkConversationCustomer = async (conversationId, data = {}) => {
  const response = await api.post(`/identity/conversations/${conversationId}/relink`, data);
  return response.data;
};

export const getConversationRepairLinks = async (conversationId, search = '') => {
  const params = {};
  if (search && search.trim()) {
    params.search = search.trim();
  }
  const response = await api.get(`/conversations/repair-links/${conversationId}`, { params });
  return response.data;
};

export const uploadConversationAttachment = async (conversationId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`/conversations/${conversationId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const transcribeAndEnhanceAudio = async (file, enhance = true, targetLanguage = null) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('enhance', String(enhance));
  if (targetLanguage) formData.append('target_language', targetLanguage);
  const response = await api.post('/conversations/audio/transcribe-enhance', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return response.data;
};

export const transcribeMessageAudioAttachment = async (conversationId, messageId, attachmentId) => {
  const response = await api.post(
    `/conversations/${conversationId}/messages/${messageId}/attachments/${attachmentId}/transcribe`,
    {},
    { timeout: 120000 }
  );
  return response.data;
};

export const enhanceTranscribedAudioText = async ({ text, sourceLanguage = null, targetLanguage = null }) => {
  const formData = new FormData();
  formData.append('text', text);
  if (sourceLanguage) formData.append('source_language', sourceLanguage);
  if (targetLanguage) formData.append('target_language', targetLanguage);
  const response = await api.post('/conversations/audio/enhance-text', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return response.data;
};

export const suggestConversationReply = async (conversationId, suggestionData = {}) => {
  const response = await api.post(`/conversations/${conversationId}/suggest-reply`, suggestionData, {
    timeout: 120000,
  });
  return response.data;
};

export const proofreadText = async (text, conversationId) => {
  const response = await api.post(
    '/conversations/proofread-text',
    { text, ...(conversationId ? { conversation_id: conversationId } : {}) },
    { timeout: 30000 }
  );
  return response.data;
};

export const translateConversation = async (conversationId, translationData = {}, requestOptions = {}) => {
  const response = await api.post(`/conversations/${conversationId}/translate`, translationData, {
    timeout: 120000,
    ...requestOptions,
  });
  return response.data;
};

export const getCommunicationEventLogs = async (params = {}) => {
  const response = await api.get('/conversations/logs/events', { params });
  return response.data;
};

export const resolveCommunicationEventLog = async (eventId, data = {}) => {
  const response = await api.post(`/conversations/logs/events/${eventId}/resolve`, data);
  return response.data;
};

// Bulk-resolve unresolved logs. Optional filters: { severity, event_type, channel, older_than_hours }
export const resolveAllCommunicationEventLogs = async (data = {}) => {
  const response = await api.post('/conversations/logs/events/resolve-all', data);
  return response.data;
};


export const getCommunicationAttachmentBlob = async (messageId, attachmentIndex, requestConfig = {}) => {
  const response = await api.get(
    `/conversations/messages/${encodeURIComponent(messageId)}/attachments/${attachmentIndex}/content`,
    { ...requestConfig, responseType: 'blob', timeout: 65000 }
  );
  return response.data;
};

export const resolveCommunicationAttachmentUrl = (url, messageId = null, attachmentIndex = null) => {
  if (!url) return '';
  const messageMediaUrl = messageId && Number.isInteger(attachmentIndex)
    ? `${BASE_URL}/conversations/messages/${encodeURIComponent(messageId)}/attachments/${attachmentIndex}/media`
    : '';
  const localAttachmentPath = /\/conversations\/attachments\/[a-f\d-]+$/i;
  // Keep audio/video sources stable when a legacy provider reference is
  // replaced by its stored copy during live conversation polling.
  if (messageMediaUrl && localAttachmentPath.test(url) && !/^https?:\/\//i.test(url)) {
    return messageMediaUrl;
  }
  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      const tunnelHost = new URL(BASE_URL, window.location.origin).hostname;
      if (
        messageMediaUrl && (
          (parsed.hostname === 'api.twilio.com'
            && /^\/2010-04-01\/Accounts\/AC[a-f\d]+\/Messages\/[a-z\d]+\/Media\/ME[a-f\d]+$/i.test(parsed.pathname))
          || ((parsed.hostname === tunnelHost || parsed.hostname === 'communication-tunnel')
            && localAttachmentPath.test(parsed.pathname))
        )
      ) {
        return messageMediaUrl;
      }
      if (parsed.hostname === 'communication-tunnel') {
        const publicPath = parsed.pathname.replace(/^\/api\/v1/, '');
        return `${BASE_URL}${publicPath}`;
      }
    } catch {
      return url;
    }
    return url;
  }
  if (url.startsWith('/api/v1/conversations/')) {
    return `${BASE_URL}${url.replace(/^\/api\/v1/, '')}`;
  }
  if (url.startsWith('/conversations/')) {
    return `${BASE_URL}${url}`;
  }
  return url;
};

export const markConversationAsRead = async (conversationId, userId) => {
  const response = await api.post(`/conversations/${conversationId}/read`, null, {
    params: { user_id: userId }
  });
  return response.data;
};

export const closeConversation = async (conversationId) => {
  const response = await api.post(`/conversations/${conversationId}/close`);
  return response.data;
};

export const hideMessage = async (messageId, userId) => {
  const response = await api.post(`/conversations/messages/${messageId}/hide`, {
    user_id: userId
  });
  return response.data;
};

export const sendEmailFromTunnel = async (conversationId, emailData) => {
  const response = await api.post(`/conversations/${conversationId}/send-email`, withAccountLanguage(emailData));
  return response.data;
};

// ============== SMS / WhatsApp / Voice (Twilio) ==============

export const sendSms = async (conversationId, smsData) => {
  const response = await api.post(`/conversations/${conversationId}/send-sms`, withAccountLanguage(smsData));
  return response.data;
};

export const getTwilioSmsConfig = async () => {
  const response = await api.get('/twilio/sms/config');
  return response.data;
};

export const sendWhatsApp = async (conversationId, waData) => {
  const response = await api.post(`/conversations/${conversationId}/send-whatsapp`, withAccountLanguage(waData));
  return response.data;
};

export const sendFacebook = async (conversationId, fbData) => {
  const response = await api.post(`/conversations/${conversationId}/send-facebook`, withAccountLanguage(fbData));
  return response.data;
};

export const sendInstagram = async (conversationId, igData) => {
  const response = await api.post(`/conversations/${conversationId}/send-instagram`, withAccountLanguage(igData));
  return response.data;
};

export const initiateCall = async (conversationId, senderUserId, senderDisplay = 'Agent') => {
  const response = await api.post(`/conversations/${conversationId}/initiate-call`, null, {
    params: { sender_user_id: senderUserId, sender_display: senderDisplay }
  });
  return response.data;
};

export const getGlobalVoiceToken = async (userId, identity) => {
  const response = await api.get('/conversations/voice-token/global', {
    params: { user_id: userId, ...(identity ? { identity } : {}) }
  });
  return response.data;
};

export const voiceAgentHeartbeat = async (identity) => {
  const response = await api.post('/conversations/voice-agent/heartbeat', null, {
    params: { identity }
  });
  return response.data;
};

export const voiceAgentLeave = async (identity) => {
  const response = await api.post('/conversations/voice-agent/leave', null, {
    params: { identity }
  });
  return response.data;
};

export const getVoiceMobileConfig = async () =>
  (await api.get('/voice-mobile/config', { timeout: 8000 })).data;

export const saveVoicePushDevice = async (data) =>
  (await api.put('/voice-mobile/devices', data, { timeout: 10000 })).data;

export const disableVoicePushDevice = async (deviceId) =>
  (await api.delete(`/voice-mobile/devices/${encodeURIComponent(deviceId)}`, { timeout: 8000 })).data;

export const getVoiceCallOffer = async (offerId) =>
  (await api.get(`/voice-mobile/offers/${encodeURIComponent(offerId)}`, { timeout: 8000 })).data;

export const getVoiceToken = async (conversationId, userId) => {
  const response = await api.get(`/conversations/${conversationId}/voice-token`, {
    params: { user_id: userId }
  });
  return response.data;
};

export const logBrowserCall = async (conversationId, callData) => {
  const response = await api.post(`/conversations/${conversationId}/log-browser-call`, callData);
  return response.data;
};

// ============== Call Records ==============

export const getCallRecording = async (callRecordId) => {
  const response = await api.get(`/calls/${callRecordId}/recording`);
  return response.data;
};

export const getCallTranscript = async (callRecordId) => {
  const response = await api.get(`/calls/${callRecordId}/transcript`);
  return response.data;
};

// Result of my own last outgoing call in a chat (Sales logs it on the deal).
export const getMyCallOutcome = async (conversationId, since) => {
  const response = await api.get('/sales/call-outcome', { params: { conversation_id: conversationId, since } });
  return response.data;
};

// ============== Missed Calls ==============

export const getMissedCallsCount = async () => {
  const response = await api.get('/conversations/missed-calls/count');
  return response.data;
};

export const getMissedCalls = async (limit = 20, unreadOnly = false, resolveLinks = false) => {
  const response = await api.get('/conversations/missed-calls/list', {
    params: { limit, unread_only: unreadOnly, resolve_links: resolveLinks }
  });
  return response.data;
};

export const markMissedCallRead = async (notificationId, userId) => {
  const response = await api.post(`/conversations/missed-calls/${notificationId}/read`, null, {
    params: { user_id: userId }
  });
  return response.data;
};

export const markAllMissedCallsRead = async (userId) => {
  const response = await api.post('/conversations/missed-calls/read-all', null, {
    params: { user_id: userId }
  });
  return response.data;
};

// Assign a chat to a person (userId) or unassign it (userId = null).
export const setConversationOwner = async (conversationId, userId, name) => {
  const response = await api.post(`/conversations/${encodeURIComponent(conversationId)}/owner`, { user_id: userId || null, name: name || null });
  return response.data;
};

// Callers whose missed call nobody has answered yet (oldest first).
export const getMissedCallCallbacks = async (days = 14) => {
  const response = await api.get('/conversations/missed-calls/callbacks', { params: { days } });
  return response.data;
};

export const markMissedCallCallbackDone = async (conversationId) => {
  const response = await api.post(`/conversations/missed-calls/callbacks/${encodeURIComponent(conversationId)}/done`);
  return response.data;
};

// ============== Caller Lookup ==============

export const callerLookup = async (phone, callSid = null) => {
  const response = await api.get('/conversations/caller-lookup', {
    params: callSid ? { call_sid: callSid } : { phone }
  });
  return response.data;
};

// ============== Contact Notes ==============

export const getContactNotes = async (phone, callSid = null) => {
  const response = await api.get('/conversations/contact-notes', {
    params: callSid ? { call_sid: callSid } : { phone }
  });
  return response.data;
};

export const createContactNote = async ({ phone, call_sid, content, customer_id, created_by_name, replace_id }) => {
  const payload = { phone, call_sid, content, customer_id, created_by_name };
  if (replace_id) payload.replace_id = replace_id;
  const response = await api.post('/conversations/contact-notes', payload);
  return response.data;
};

// ============== Recording Proxy ==============

export const getRecordingProxyUrl = (callRecordId) => {
  const token = localStorage.getItem('token');
  const authParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${BASE_URL}/conversations/recording/${callRecordId}${authParam}`;
};

export const transcribeCallRecording = async (callRecordId, force = false) => {
  const response = await api.post(`/conversations/recording/${callRecordId}/transcribe`, null, {
    params: { force },
    timeout: 120000,
  });
  return response.data;
};

export const generateCallCoachFeedback = async (callRecordId, force = false) => {
  const response = await api.post(
    `/conversations/call-history/${encodeURIComponent(callRecordId)}/coach`,
    null,
    { params: { force }, timeout: 90000 },
  );
  return response.data;
};

export const getCallGroupFeedback = async (callRecordId) => {
  const response = await api.get(
    `/conversations/call-history/${encodeURIComponent(callRecordId)}/group-feedback`,
  );
  return response.data;
};

export const addCallGroupFeedback = async (callRecordId, content) => {
  const response = await api.post(
    `/conversations/call-history/${encodeURIComponent(callRecordId)}/group-feedback`,
    { content },
  );
  return response.data;
};

// ============== Call History ==============

export const getMessageAnalytics = async (groupBy = 'day', startDate = null, endDate = null) => {
  const params = { group_by: groupBy };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get('/conversations/message-analytics', { params });
  return response.data;
};

export const getCallAnalytics = async (groupBy = 'day', startDate = null, endDate = null, direction = null) => {
  const params = { group_by: groupBy };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (direction) params.direction = direction;
  const response = await api.get('/conversations/call-analytics', { params });
  return response.data;
};

export const getCallCosts = async (startDate = null, endDate = null) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return (await api.get('/conversations/call-costs', { params, timeout: 30000 })).data;
};

export const getCallHistory = async (page = 1, pageSize = 20, direction = null, statusFilter = null, phone = null, search = null, signal = undefined) => {
  const params = { page, page_size: pageSize };
  if (direction) params.direction = direction;
  if (statusFilter) params.status_filter = statusFilter;
  if (phone) params.phone = phone;
  if (search) params.search = search;
  const response = await api.get('/conversations/call-history', { params, signal });
  return response.data;
};

// ============== VAPI Voice AI integration ==============

export const getVapiStatus = async () => {
  const response = await api.get('/vapi/status');
  return response.data;
};

export const saveVapiConfig = async ({ private_api_key = null, webhook_secret = null }) => {
  const response = await api.post('/vapi/config', { private_api_key, webhook_secret });
  return response.data;
};

// ============== AI Response Assistant & Continuous Learning ==============

export const generateAiResponseDraft = async ({ conversation_id, channel = 'WHATSAPP', reseller_context = null }) => {
  const response = await api.post('/communication-tunnel/ai-assistant/generate-draft', {
    conversation_id,
    channel,
    reseller_context
  });
  return response.data;
};

export const submitAiResponseFeedback = async ({
  conversation_id,
  draft_id = null,
  original_ai_text,
  final_sent_text,
  feedback_category = 'AGENT_EDITED',
  user_comments = null,
  user_id = null
}) => {
  const response = await api.post('/communication-tunnel/ai-assistant/submit-feedback', {
    conversation_id,
    draft_id,
    original_ai_text,
    final_sent_text,
    feedback_category,
    user_comments,
    user_id
  });
  return response.data;
};

export const getLearningRecommendations = async (status = 'PENDING_REVIEW') => {
  const response = await api.get('/communication-tunnel/ai-assistant/recommendations', {
    params: { status }
  });
  return response.data;
};

export const approveLearningRecommendation = async (rec_id, user_id = 'admin') => {
  const response = await api.post(`/communication-tunnel/ai-assistant/recommendations/${rec_id}/approve`, {
    user_id
  });
  return response.data;
};

// ============== Global & Conversation Chatbot Toggle ==============

export const getGlobalChatbotSettings = async () => {
  const response = await api.get('/chatbot/global/settings');
  return response.data;
};

export const updateGlobalChatbotSettings = async (enabled) => {
  const response = await api.put('/chatbot/global/settings', { enabled });
  return response.data;
};

// ============== Chatbot Training ==============

// Approved WhatsApp templates per chatbot step.
export const getBotTemplateCoverage = async () => {
  const response = await api.get('/chatbot/templates/coverage');
  return response.data;
};

// Bot steps whose WhatsApp template still needs approval (admin warning).
export const getBotTemplateAttention = async () => {
  const response = await api.get('/chatbot/templates/attention');
  return response.data;
};

export const updateBotTemplatePolicy = async (templatesOnly) => {
  const response = await api.put('/chatbot/templates/policy', { templates_only: templatesOnly });
  return response.data;
};

export const updateBotStepTemplate = async (stepKey, language, contentSid, name) => {
  const response = await api.put(
    `/chatbot/templates/steps/${encodeURIComponent(stepKey)}/${encodeURIComponent(language)}`,
    { content_sid: contentSid, name },
  );
  return response.data;
};

export const getChatbotTrainingTemplates = async () => {
  const response = await api.get('/chatbot/training/templates');
  return response.data;
};

export const updateChatbotTrainingTemplate = async (templateKey, language, content) => {
  const response = await api.put(
    `/chatbot/training/templates/${encodeURIComponent(templateKey)}/${encodeURIComponent(language)}`,
    { content },
  );
  return response.data;
};

export const resetChatbotTrainingTemplate = async (templateKey, language) => {
  const response = await api.delete(
    `/chatbot/training/templates/${encodeURIComponent(templateKey)}/${encodeURIComponent(language)}`,
  );
  return response.data;
};

export const getChatbotTrainingKnowledge = async () => {
  const response = await api.get('/chatbot/training/knowledge');
  return response.data;
};

export const createChatbotTrainingKnowledge = async (payload) => {
  const response = await api.post('/chatbot/training/knowledge', payload);
  return response.data;
};

export const updateChatbotTrainingKnowledge = async (entryId, payload) => {
  const response = await api.put(`/chatbot/training/knowledge/${entryId}`, payload);
  return response.data;
};

export const deleteChatbotTrainingKnowledge = async (entryId) => {
  const response = await api.delete(`/chatbot/training/knowledge/${entryId}`);
  return response.data;
};

export const testChatbotTraining = async (payload) => {
  const response = await api.post('/chatbot/training/test', payload, { timeout: 120000 });
  return response.data;
};

export const getConversationChatbotStatus = async (conversationId) => {
  const response = await api.get(`/chatbot/conversations/${conversationId}/status`);
  return response.data;
};

export const toggleConversationChatbot = async (conversationId, active) => {
  const response = await api.put(`/chatbot/conversations/${conversationId}/toggle`, { active });
  return response.data;
};


// ── Guards against the /call-history flood (P1 comm-tunnel health incident) ──
// Identical concurrent requests for the same phone share one Promise, and
// results are cached briefly so repeated re-renders don't re-hit the server.
const _callsByPhonePending = new Map();
const _callsByPhoneCache = new Map();
const CALLS_BY_PHONE_TTL_MS = 30000;

/**
 * Fetch all calls linked to a specific customer phone number.
 * Server-side filtering is used when available; client-side matching remains
 * as a fallback for deployments that do not yet support the phone parameter.
 * Concurrent identical calls are de-duplicated and results cached for ~30s.
 * Pass { force: true } (e.g. a manual Refresh) to bypass the cache/dedup.
 */
export const getCallsByPhone = async (phone, { force = false } = {}) => {
  if (!phone) return [];
  const phoneCandidates = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (digits.length < 6) return [];

    const candidates = new Set([digits]);
    if (digits.startsWith('00') && digits.length > 8) {
      candidates.add(digits.slice(2));
    }
    if (digits.startsWith('0') && digits.length >= 9) {
      candidates.add(`41${digits.slice(1)}`);
    }
    if (digits.startsWith('41') && digits.length >= 10) {
      candidates.add(`0${digits.slice(2)}`);
    }
    if (digits.length >= 9) {
      candidates.add(digits.slice(-9));
    }

    return Array.from(candidates).filter(candidate => candidate.length >= 6);
  };

  const targetCandidates = phoneCandidates(phone);
  if (targetCandidates.length === 0) return [];

  const phonesMatch = (a, bCandidates) => {
    const aCandidates = phoneCandidates(a);
    return aCandidates.some(left =>
      bCandidates.some(right =>
        left === right ||
        (left.length >= 9 && right.length >= 9 && (left.endsWith(right) || right.endsWith(left)))
      )
    );
  };

  const key = String(phone).replace(/\D/g, '');
  if (key.length < 6) return [];

  if (!force) {
    const cached = _callsByPhoneCache.get(key);
    if (cached && Date.now() - cached.at < CALLS_BY_PHONE_TTL_MS) return cached.value;
    if (_callsByPhonePending.has(key)) return _callsByPhonePending.get(key);
  }

  const request = (async () => {
    try {
      const data = await getCallHistory(1, 50, null, null, phone);
      const items = data.items || [];
      const value = items.filter(call => phonesMatch(call.customer_phone || '', targetCandidates));
      _callsByPhoneCache.set(key, { at: Date.now(), value });
      return value;
    } catch (err) {
      console.warn('[communicationApi] getCallsByPhone failed:', err);
      return [];
    } finally {
      _callsByPhonePending.delete(key);
    }
  })();

  _callsByPhonePending.set(key, request);
  return request;
};


// ============== Direct Dial ==============

export const dialDirect = async ({ phone_number, user_id, user_display = 'Agent', actor_id = null }) => {
  const payload = { phone_number, user_id, user_display };
  if (actor_id) payload.actor_id = actor_id;
  const response = await api.post('/conversations/dial-direct', payload);
  return response.data;
};

// ============== Stats ==============

export const getStats = async () => {
  const response = await api.get('/stats');
  return response.data;
};

// Channel types for filtering
export const ChannelType = {
  EMAIL: 'EMAIL',
  PHONE: 'PHONE',
  WHATSAPP: 'WHATSAPP',
  INSTAGRAM: 'INSTAGRAM',
  FACEBOOK: 'FACEBOOK',
  CHAT: 'CHAT',
  SMS: 'SMS',
  NOTE: 'NOTE',
  SYSTEM: 'SYSTEM'
};

export const ConversationStatus = {
  ACTIVE: 'ACTIVE',
  CLOSED: 'CLOSED',
  ARCHIVED: 'ARCHIVED'
};

export const MessageDirection = {
  INBOUND: 'INBOUND',
  OUTBOUND: 'OUTBOUND'
};

// ============== Scheduled Messages ==============

export const scheduleMessage = async (scheduledData) => {
  const response = await api.post('/conversations/scheduled-messages', withAccountLanguage(scheduledData));
  return response.data;
};

export const getScheduledMessages = async (conversationId, status = 'PENDING') => {
  const response = await api.get(`/conversations/${conversationId}/scheduled-messages`, {
    params: { status }
  });
  return response.data;
};

export const cancelScheduledMessage = async (scheduledId) => {
  const response = await api.delete(`/conversations/scheduled-messages/${scheduledId}`);
  return response.data;
};

export const updateScheduledMessage = async (scheduledId, scheduledData) => {
  const response = await api.put(`/conversations/scheduled-messages/${scheduledId}`, withAccountLanguage(scheduledData));
  return response.data;
};

export const getCoachKnowledge = async (role) => (await api.get('/conversations/coaching/knowledge', { params: { role } })).data;
export const reviewCoachKnowledge = async (knowledgeId, payload) => (await api.post(`/conversations/coaching/knowledge/${encodeURIComponent(knowledgeId)}/review`, payload)).data;
export const getLiveCoach = async (conversationId, callSid, revision, signal) => (await api.get(`/conversations/coaching/live/${conversationId}`, { params: { call_sid: callSid, revision }, signal })).data;
export const setLiveCoachMode = async (sessionId, mode) => (await api.put(`/conversations/coaching/live-sessions/${sessionId}/mode`, { mode })).data;
export const sendLiveCoachFeedback = async (sessionId, knowledgeId, action) => (await api.post(`/conversations/coaching/live-sessions/${sessionId}/feedback`, { knowledge_id: knowledgeId, action })).data;
export const getTrainingExamples = async () => (await api.get('/conversations/coaching/examples', { params: { limit: 100 } })).data;
export const createTrainingExample = async (callId, payload) => (await api.post(`/conversations/coaching/calls/${callId}/examples`, payload)).data;
export const editTrainingExample = async (id, payload) => (await api.put(`/conversations/coaching/examples/${id}`, payload)).data;
export const reviewTrainingExample = async (id, payload) => (await api.post(`/conversations/coaching/examples/${id}/reviews`, payload)).data;
export const getTrainingDatasets = async () => (await api.get('/conversations/coaching/datasets')).data;
export const getTrainingUses = async (id) => (await api.get(`/conversations/coaching/datasets/${id}/uses`)).data;
export const registerTrainingUse = async (id, payload) => (await api.post(`/conversations/coaching/datasets/${id}/uses`, payload)).data;
export const updateTrainingUse = async (id, status) => (await api.put(`/conversations/coaching/uses/${id}`, { status })).data;
export const getTrainingEvaluationCases = async (id) => (await api.get(`/conversations/coaching/datasets/${id}/evaluation-cases`)).data;
export const getTrainingEvaluations = async (id) => (await api.get(`/conversations/coaching/datasets/${id}/evaluations`)).data;
export const getTrainingEvaluation = async (id) => (await api.get(`/conversations/coaching/evaluations/${id}`)).data;
export const saveTrainingEvaluation = async (id, payload) => (await api.post(`/conversations/coaching/datasets/${id}/evaluations`, payload)).data;
export const getTrainingDatasetReport = async (id) => (await api.get(`/conversations/coaching/datasets/${id}/report`)).data;
export const createTrainingDataset = async (payload) => (await api.post('/conversations/coaching/datasets', payload)).data;
export const exportTrainingDataset = async (id, split) => (await api.get(`/conversations/coaching/datasets/${id}/export`, { params: { split }, responseType: 'blob' })).data;

export default {
  createAlias,
  getAlias,
  getAliasByCode,
  getAliasByCustomer,
  lookupOrCreateAlias,
  getUnifiedInbox,
  createConversation,
  getConversation,
  getConversationsForCustomer,
  getConversationsForProcess,
  getConversationMessages,
  getConversationMessageAnchor,
  sendMessage,
  scheduleMessage,
  getScheduledMessages,
  cancelScheduledMessage,
  updateScheduledMessage,
  updateMessageTriage,
  setMessageReaction,
  updateConversationTriage,
  getConversationRepairLinks,
  uploadConversationAttachment,
  enhanceTranscribedAudioText,
  transcribeMessageAudioAttachment,
  suggestConversationReply,
  proofreadText,
  translateConversation,
  getCommunicationEventLogs,
  resolveCommunicationEventLog,
  resolveCommunicationAttachmentUrl,
  getCommunicationAttachmentBlob,
  markConversationAsRead,
  closeConversation,
  hideMessage,
  sendEmailFromTunnel,
  sendSms,
  getTwilioSmsConfig,
  sendWhatsApp,
  sendFacebook,
  sendInstagram,
  initiateCall,
  dialDirect,
  logBrowserCall,
  getCallHistory,
  getCallsByPhone,
  getCallRecording,
  getCallTranscript,
  getRecordingProxyUrl,
  transcribeCallRecording,
  getStats,
  callerLookup,
  getContactNotes,
  createContactNote,
  ChannelType,
  ConversationStatus,
  MessageDirection
};



// Internal ERP Connect channels. These routes never fan out to customer providers.
export const getConnectChannelCapabilities = async (config = {}) =>
  (await api.get('/meetings/capabilities', config)).data;
export const getMeetingContextForConversation = async (conversationId, config = {}) =>
  (await api.get(`/meetings/for-conversation/${encodeURIComponent(conversationId)}`, config)).data;
export const createMeetingContext = async (payload, config = {}) =>
  (await api.post('/meetings', payload, config)).data;
export const getStandaloneMeetings = async (config = {}) =>
  (await api.get('/meetings/standalone/owned', config)).data;
export const createStandaloneMeeting = async (payload, config = {}) =>
  (await api.post('/meetings/standalone', payload, config)).data;
export const getMeetingCalendarFile = async (meetingId, config = {}) =>
  (await api.get(`/meetings/${encodeURIComponent(meetingId)}/calendar.ics`, { ...config, responseType: 'blob' })).data;
export const getMeetingContext = async (meetingId, config = {}) =>
  (await api.get(`/meetings/${encodeURIComponent(meetingId)}`, config)).data;
export const createSharedMeetingLink = async (meetingId, policyRevision, expiresAt, config = {}) =>
  (await api.post(`/meetings/${encodeURIComponent(meetingId)}/shared-link`,
    { policy_revision: policyRevision, expires_at: expiresAt }, config)).data;
export const revokeSharedMeetingLink = async (meetingId, policyRevision, config = {}) =>
  (await api.post(`/meetings/${encodeURIComponent(meetingId)}/shared-link/revoke`,
    { policy_revision: policyRevision }, config)).data;
export const getMeetingLobby = async (meetingId, policyRevision, config = {}) =>
  (await api.get(`/meetings/${encodeURIComponent(meetingId)}/lobby`,
    { ...config, params: { policy_revision: policyRevision } })).data;
export const decideMeetingLobbyRequest = async (meetingId, requestId, policyRevision, action, config = {}) =>
  (await api.post(`/meetings/${encodeURIComponent(meetingId)}/lobby/${encodeURIComponent(requestId)}/decision`,
    { policy_revision: policyRevision, action }, config)).data;
export const requestSharedMeetingEntry = async (meetingId, token, requestToken, config = {}, displayName = '') =>
  (await guestApi.post(`/meetings/${encodeURIComponent(meetingId)}/join-requests`,
    { token, request_token: requestToken, ...(displayName ? { display_name: displayName } : {}) }, config)).data;
export const getSharedMeetingEntryStatus = async (meetingId, requestId, requestToken, config = {}) =>
  (await guestApi.get(`/meetings/${encodeURIComponent(meetingId)}/lobby/${encodeURIComponent(requestId)}`,
    guestMeetingConfig(requestToken, config))).data;
export const claimSharedMeetingEntry = async (meetingId, requestId, requestToken, config = {}) =>
  (await guestApi.post(`/meetings/${encodeURIComponent(meetingId)}/lobby/${encodeURIComponent(requestId)}/claim`,
    {}, guestMeetingConfig(requestToken, config))).data;
export const changeMeetingState = async (meetingId, policyRevision, state, config = {}) =>
  (await api.post(`/meetings/${encodeURIComponent(meetingId)}/state`,
    { policy_revision: policyRevision, state }, config)).data;
export const createConnectMeetingInvitation = async (meetingId, policyRevision, expiresAt, config = {}) =>
  (await api.post(`/meetings/${encodeURIComponent(meetingId)}/invitations`,
    { policy_revision: policyRevision, expires_at: expiresAt }, config)).data;
export const revokeConnectMeetingInvitation = async (meetingId, invitationId, policyRevision, config = {}) =>
  (await api.post(`/meetings/${encodeURIComponent(meetingId)}/invitations/${encodeURIComponent(invitationId)}/revoke`,
    { policy_revision: policyRevision }, config)).data;
const connectMeetingMediaPath = meetingId => `/meetings/${encodeURIComponent(meetingId)}/media`;
export const setMeetingMode = async (meetingId, payload, config = {}) =>
  (await api.post(`${connectMeetingMediaPath(meetingId)}/mode`, payload, config)).data;
export const grantStageSpeaker = async (meetingId, memberId, minutes = null, config = {}) =>
  (await api.post(`${connectMeetingMediaPath(meetingId)}/stage/speakers`,
    { member_id: memberId, minutes }, config)).data;
export const revokeStageSpeaker = async (meetingId, memberId, config = {}) =>
  (await api.delete(`${connectMeetingMediaPath(meetingId)}/stage/speakers/${encodeURIComponent(memberId)}`, config)).data;
export const setStageHand = async (meetingId, raised, config = {}) =>
  (await api.post(`${connectMeetingMediaPath(meetingId)}/stage/hand`, { raised }, config)).data;
export const getConnectMeetingMediaParticipants = async (meetingId, config = {}) =>
  (await api.get(`${connectMeetingMediaPath(meetingId)}/participants`, config)).data;
export const sendConnectMeetingMediaSignal = async (meetingId, payload, config = {}) =>
  (await api.post(`${connectMeetingMediaPath(meetingId)}/signals`, payload, config)).data;
export const pollConnectMeetingMediaSignals = async (meetingId, after = 0, config = {}) =>
  (await api.get(`${connectMeetingMediaPath(meetingId)}/signals`, { ...config, params: { after } })).data;
/**
 * The STUN and TURN servers this browser needs to reach the other side.
 *
 * Issued per request and short-lived, so they are fetched when a call starts
 * rather than kept in the page.
 */
export const getConnectMeetingMediaIce = async (meetingId, config = {}) =>
  (await api.get(`${connectMeetingMediaPath(meetingId)}/ice`, config)).data;
const connectGuestMeetingMediaPath = meetingId => `${connectMeetingMediaPath(meetingId)}/guest`;
export const setGuestStageHand = async (meetingId, sessionToken, raised, config = {}) =>
  (await guestApi.post(`${connectGuestMeetingMediaPath(meetingId)}/stage/hand`, { raised },
    { ...config, headers: { ...(config.headers || {}), Authorization: `Bearer ${sessionToken}` } })).data;
export const getConnectGuestMeetingMediaParticipants = async (meetingId, sessionToken, config = {}) =>
  (await guestApi.get(`${connectGuestMeetingMediaPath(meetingId)}/participants`,
    guestMeetingConfig(sessionToken, config))).data;
export const sendConnectGuestMeetingMediaSignal = async (meetingId, sessionToken, payload, config = {}) =>
  (await guestApi.post(`${connectGuestMeetingMediaPath(meetingId)}/signals`, payload,
    guestMeetingConfig(sessionToken, config))).data;
export const getConnectGuestMeetingMediaIce = async (meetingId, sessionToken, config = {}) =>
  (await guestApi.get(`${connectGuestMeetingMediaPath(meetingId)}/ice`,
    guestMeetingConfig(sessionToken, config))).data;
export const pollConnectGuestMeetingMediaSignals = async (meetingId, sessionToken, after = 0, config = {}) =>
  (await guestApi.get(`${connectGuestMeetingMediaPath(meetingId)}/signals`,
    { ...guestMeetingConfig(sessionToken, config), params: { after } })).data;
const connectMeetingParticipationPath = meetingId =>
  `/meetings/${encodeURIComponent(meetingId)}/participation`;
export const getConnectMeetingSpeakingQueue = async (meetingId, config = {}) =>
  (await api.get(`${connectMeetingParticipationPath(meetingId)}/speaking-queue`, config)).data;
export const setConnectOwnRaisedHand = async (meetingId, payload, config = {}) =>
  (await api.put(`${connectMeetingParticipationPath(meetingId)}/speaking-queue/own`, payload, config)).data;
export const clearConnectRaisedHand = async (meetingId, payload, config = {}) =>
  (await api.post(`${connectMeetingParticipationPath(meetingId)}/speaking-queue/clear`, payload, config)).data;
export const getConnectMeetingHelpRequests = async (meetingId, config = {}) =>
  (await api.get(`${connectMeetingParticipationPath(meetingId)}/help-requests`, config)).data;
export const requestConnectMeetingHelp = async (meetingId, payload, config = {}) =>
  (await api.post(`${connectMeetingParticipationPath(meetingId)}/help-requests`, payload, config)).data;
export const withdrawConnectMeetingHelp = async (meetingId, requestId, revision, config = {}) =>
  (await api.delete(`${connectMeetingParticipationPath(meetingId)}/help-requests/${encodeURIComponent(requestId)}`,
    { ...config, params: { ...(config.params || {}), policy_revision: revision } })).data;
export const resolveConnectMeetingHelp = async (meetingId, requestId, revision, config = {}) =>
  (await api.post(`${connectMeetingParticipationPath(meetingId)}/help-requests/${encodeURIComponent(requestId)}/resolve`,
    null, { ...config, params: { ...(config.params || {}), policy_revision: revision } })).data;
const connectGuestPollPath = meetingId => `/meetings/${encodeURIComponent(meetingId)}/polls/guest`;
const connectMeetingPollPath = meetingId => `/meetings/${encodeURIComponent(meetingId)}/polls`;
const connectMeetingQuestionsPath = meetingId => `/meetings/${encodeURIComponent(meetingId)}/questions`;
const connectGuestQuestionsPath = meetingId => `${connectMeetingQuestionsPath(meetingId)}/guest`;
const connectGuestLifecyclePath = meetingId => `/meetings/${encodeURIComponent(meetingId)}`;
const guestMeetingConfig = (sessionToken, config = {}) => ({
  ...config,
  headers: { ...(config.headers || {}), Authorization: `Bearer ${sessionToken}` },
});
export const getMeetingAnalytics = async (params, config = {}) =>
  (await api.get('/meetings/analytics/summary', { ...config, params })).data;
const transcriptForm = (blob, durationMs, spokenMs = 0) => {
  const form = new FormData();
  form.append('file', blob, 'speech');
  form.append('duration_ms', String(Math.round(durationMs || 0)));
  form.append('spoken_ms', String(Math.round(spokenMs || 0)));
  return form;
};
export const sendMeetingTranscriptChunk = async (meetingId, blob, durationMs, config = {}, spokenMs = 0) =>
  (await api.post(`/meetings/${encodeURIComponent(meetingId)}/transcript/chunks`, transcriptForm(blob, durationMs, spokenMs),
    { timeout: 120000, ...config, headers: { 'Content-Type': 'multipart/form-data', ...(config.headers || {}) } })).data;
export const sendGuestMeetingTranscriptChunk = async (meetingId, sessionToken, blob, durationMs, config = {}, spokenMs = 0) =>
  (await guestApi.post(`/meetings/${encodeURIComponent(meetingId)}/transcript/guest/chunks`, transcriptForm(blob, durationMs, spokenMs),
    guestMeetingConfig(sessionToken, { timeout: 120000, ...config,
      headers: { 'Content-Type': 'multipart/form-data', ...(config.headers || {}) } }))).data;
export const getGuestMeetingTranscript = async (meetingId, sessionToken, config = {}) =>
  (await guestApi.get(`/meetings/${encodeURIComponent(meetingId)}/transcript/guest`, guestMeetingConfig(sessionToken, config))).data;
export const getMeetingTranscript = async (meetingId, config = {}) =>
  (await api.get(`/meetings/${encodeURIComponent(meetingId)}/transcript`, config)).data;
export const admitConnectGuestMeeting = async (meetingId, invitationToken, config = {}, displayName = '') =>
  (await guestApi.post(`${connectGuestLifecyclePath(meetingId)}/admission/guest`,
    { token: invitationToken, ...(displayName ? { display_name: displayName } : {}) }, config)).data;
export const getConnectGuestMeetingContext = async (meetingId, sessionToken, config = {}) =>
  (await guestApi.get(`${connectGuestLifecyclePath(meetingId)}/guest-context`,
    guestMeetingConfig(sessionToken, config))).data;
export const getConnectGuestMeetingPolls = async (meetingId, sessionToken, config = {}) =>
  (await guestApi.get(connectGuestPollPath(meetingId), guestMeetingConfig(sessionToken, config))).data;
export const answerConnectGuestMeetingPoll = async (meetingId, pollId, sessionToken, payload, config = {}) =>
  (await guestApi.post(`${connectGuestPollPath(meetingId)}/${encodeURIComponent(pollId)}/responses`, payload,
    guestMeetingConfig(sessionToken, config))).data;
export const getConnectGuestMeetingQuestions = async (meetingId, sessionToken, config = {}) =>
  (await guestApi.get(connectGuestQuestionsPath(meetingId), guestMeetingConfig(sessionToken, config))).data;
export const submitConnectGuestMeetingQuestion = async (meetingId, sessionToken, payload, config = {}) =>
  (await guestApi.post(connectGuestQuestionsPath(meetingId), payload, guestMeetingConfig(sessionToken, config))).data;
export const withdrawConnectGuestMeetingQuestion = async (meetingId, questionId, sessionToken, payload, config = {}) =>
  (await guestApi.post(`${connectGuestQuestionsPath(meetingId)}/${encodeURIComponent(questionId)}/withdraw`, payload,
    guestMeetingConfig(sessionToken, config))).data;
export const getConnectMeetingPolls = async (meetingId, config = {}) =>
  (await api.get(connectMeetingPollPath(meetingId), config)).data;
export const createConnectMeetingPoll = async (meetingId, payload, config = {}) =>
  (await api.post(connectMeetingPollPath(meetingId), payload, config)).data;
export const openConnectMeetingPoll = async (meetingId, pollId, payload, config = {}) =>
  (await api.post(`${connectMeetingPollPath(meetingId)}/${encodeURIComponent(pollId)}/open`, payload, config)).data;
export const answerConnectMeetingPoll = async (meetingId, pollId, payload, config = {}) =>
  (await api.post(`${connectMeetingPollPath(meetingId)}/${encodeURIComponent(pollId)}/responses`, payload, config)).data;
export const closeConnectMeetingPoll = async (meetingId, pollId, payload, config = {}) =>
  (await api.post(`${connectMeetingPollPath(meetingId)}/${encodeURIComponent(pollId)}/close`, payload, config)).data;
export const releaseConnectMeetingPollResults = async (meetingId, pollId, payload, config = {}) =>
  (await api.post(`${connectMeetingPollPath(meetingId)}/${encodeURIComponent(pollId)}/release-results`, payload, config)).data;
export const getConnectMeetingQuestions = async (meetingId, config = {}) =>
  (await api.get(connectMeetingQuestionsPath(meetingId), config)).data;
export const submitConnectMeetingQuestion = async (meetingId, payload, config = {}) =>
  (await api.post(connectMeetingQuestionsPath(meetingId), payload, config)).data;
export const approveConnectMeetingQuestion = async (meetingId, questionId, payload, config = {}) =>
  (await api.post(`${connectMeetingQuestionsPath(meetingId)}/${encodeURIComponent(questionId)}/approve`, payload, config)).data;
export const answerConnectMeetingQuestion = async (meetingId, questionId, payload, config = {}) =>
  (await api.post(`${connectMeetingQuestionsPath(meetingId)}/${encodeURIComponent(questionId)}/answer`, payload, config)).data;
export const mergeConnectMeetingQuestion = async (meetingId, questionId, payload, config = {}) =>
  (await api.post(`${connectMeetingQuestionsPath(meetingId)}/${encodeURIComponent(questionId)}/merge`, payload, config)).data;
export const withdrawConnectMeetingQuestion = async (meetingId, questionId, payload, config = {}) =>
  (await api.post(`${connectMeetingQuestionsPath(meetingId)}/${encodeURIComponent(questionId)}/withdraw`, payload, config)).data;
export const getConnectChannelWorkspace = async (conversationId, config = {}) =>
  (await api.get(`/meetings/collaboration/for-conversation/${encodeURIComponent(conversationId)}`, config)).data;
const connectChannelPath = (meetingId, channelId) =>
  `/meetings/${encodeURIComponent(meetingId)}/channels${channelId ? `/${encodeURIComponent(channelId)}` : ''}`;
export const createConnectChannel = async (meetingId, payload, config = {}) =>
  (await api.post(connectChannelPath(meetingId), payload, config)).data;
export const getConnectChannelPosts = async (meetingId, channelId, params = {}, config = {}) =>
  (await api.get(`${connectChannelPath(meetingId, channelId)}/posts`, { ...config, params })).data;
export const sendConnectChannelPost = async (meetingId, channelId, payload, config = {}) =>
  (await api.post(`${connectChannelPath(meetingId, channelId)}/posts`, payload, config)).data;
export const editConnectChannelPost = async (meetingId, channelId, postId, payload, config = {}) =>
  (await api.patch(`${connectChannelPath(meetingId, channelId)}/posts/${encodeURIComponent(postId)}`, payload, config)).data;
export const withdrawConnectChannelPost = async (meetingId, channelId, postId, payload, config = {}) =>
  (await api.post(`${connectChannelPath(meetingId, channelId)}/posts/${encodeURIComponent(postId)}/withdraw`, payload, config)).data;
export const getConnectChannelDraft = async (meetingId, channelId, config = {}) =>
  (await api.get(`${connectChannelPath(meetingId, channelId)}/own-draft`, config)).data;
export const saveConnectChannelDraft = async (meetingId, channelId, payload, config = {}) =>
  (await api.put(`${connectChannelPath(meetingId, channelId)}/own-draft`, payload, config)).data;
export const leaveConnectChannel = async (meetingId, channelId, payload, config = {}) =>
  (await api.post(`${connectChannelPath(meetingId, channelId)}/leave`, payload, config)).data;

export const getConnectChannelManagement = async (meetingId, channelId, config = {}) =>
  (await api.get(`${connectChannelPath(meetingId, channelId)}/management`, config)).data;
export const getConnectChannelMentionTargets = async (meetingId, channelId, config = {}) =>
  (await api.get(`${connectChannelPath(meetingId, channelId)}/mention-targets`, config)).data;
export const getConnectChannelBookmarks = async (meetingId, channelId, config = {}) =>
  (await api.get(`${connectChannelPath(meetingId, channelId)}/bookmarks`, config)).data;
export const setConnectPostBookmark = async (meetingId, channelId, postId, payload, config = {}) =>
  (await api.put(`${connectChannelPath(meetingId, channelId)}/posts/${encodeURIComponent(postId)}/bookmark`, payload, config)).data;
export const getConnectChannelReadingState = async (meetingId, channelId, config = {}) =>
  (await api.get(`${connectChannelPath(meetingId, channelId)}/reading-state`, config)).data;
export const markConnectChannelPostSeen = async (meetingId, channelId, payload, config = {}) =>
  (await api.post(`${connectChannelPath(meetingId, channelId)}/reading-state`, payload, config)).data;
export const addConnectChannelMember = async (meetingId, channelId, payload, config = {}) =>
  (await api.post(`${connectChannelPath(meetingId, channelId)}/members`, payload, config)).data;
export const batchAddConnectBreakoutMembers = async (meetingId, channelId, payload, config = {}) =>
  (await api.post(`${connectChannelPath(meetingId, channelId)}/members/batch`, payload, config)).data;
export const removeConnectChannelMember = async (meetingId, channelId, participantId, payload, config = {}) =>
  (await api.post(`${connectChannelPath(meetingId, channelId)}/members/${encodeURIComponent(participantId)}/remove`, payload, config)).data;
export const archiveConnectChannel = async (meetingId, channelId, payload, config = {}) =>
  (await api.post(`${connectChannelPath(meetingId, channelId)}/archive`, payload, config)).data;

export const getConnectPostReactions = async (meetingId, channelId, postId, config = {}) =>
  (await api.get(`${connectChannelPath(meetingId, channelId)}/posts/${encodeURIComponent(postId)}/reactions`, config)).data;
export const setConnectPostReaction = async (meetingId, channelId, postId, payload, config = {}) =>
  (await api.put(`${connectChannelPath(meetingId, channelId)}/posts/${encodeURIComponent(postId)}/reactions/own`, payload, config)).data;

export const getConnectPrivateNote = async (meetingId, channelId, config = {}) =>
  (await api.get(`${connectChannelPath(meetingId, channelId)}/own-note`, config)).data;
export const saveConnectPrivateNote = async (meetingId, channelId, payload, config = {}) =>
  (await api.put(`${connectChannelPath(meetingId, channelId)}/own-note`, payload, config)).data;

const connectSharedDocumentPath = (meetingId, channelId) =>
  `${connectChannelPath(meetingId, channelId)}/documents`;
export const getConnectSharedDocuments = async (meetingId, channelId, config = {}) =>
  (await api.get(connectSharedDocumentPath(meetingId, channelId), config)).data;
export const createConnectSharedDocument = async (meetingId, channelId, payload, config = {}) =>
  (await api.post(connectSharedDocumentPath(meetingId, channelId), payload, config)).data;
export const updateConnectSharedDocument = async (meetingId, channelId, documentId, payload, config = {}) =>
  (await api.patch(`${connectSharedDocumentPath(meetingId, channelId)}/${encodeURIComponent(documentId)}`,
    payload, config)).data;
export const setConnectSharedDocumentEditing = async (meetingId, channelId, documentId, payload, config = {}) =>
  (await api.patch(`${connectSharedDocumentPath(meetingId, channelId)}/${encodeURIComponent(documentId)}/collaborative-editing`,
    payload, config)).data;
export const getConnectSharedDocumentHistory = async (meetingId, channelId, documentId, params = {}, config = {}) =>
  (await api.get(`${connectSharedDocumentPath(meetingId, channelId)}/${encodeURIComponent(documentId)}/history`,
    { ...config, params })).data;
export const getConnectSharedDocumentComments = async (meetingId, channelId, documentId, config = {}) =>
  (await api.get(`${connectSharedDocumentPath(meetingId, channelId)}/${encodeURIComponent(documentId)}/comments`, config)).data;
export const addConnectSharedDocumentComment = async (meetingId, channelId, documentId, payload, config = {}) =>
  (await api.post(`${connectSharedDocumentPath(meetingId, channelId)}/${encodeURIComponent(documentId)}/comments`,
    payload, config)).data;

const connectWhiteboardPath = (meetingId, channelId) =>
  `${connectChannelPath(meetingId, channelId)}/whiteboards`;
export const getConnectWhiteboards = async (meetingId, channelId, config = {}) =>
  (await api.get(connectWhiteboardPath(meetingId, channelId), config)).data;
export const createConnectWhiteboard = async (meetingId, channelId, payload, config = {}) =>
  (await api.post(connectWhiteboardPath(meetingId, channelId), payload, config)).data;
export const getConnectWhiteboardOperations = async (meetingId, channelId, pageId, params = {}, config = {}) =>
  (await api.get(`${connectWhiteboardPath(meetingId, channelId)}/${encodeURIComponent(pageId)}/operations`,
    { ...config, params })).data;
export const addConnectWhiteboardOperation = async (meetingId, channelId, pageId, payload, config = {}) =>
  (await api.post(`${connectWhiteboardPath(meetingId, channelId)}/${encodeURIComponent(pageId)}/operations`, payload, config)).data;
export const createConnectWhiteboardExport = async (meetingId, channelId, pageId, payload, config = {}) =>
  (await api.post(`${connectWhiteboardPath(meetingId, channelId)}/${encodeURIComponent(pageId)}/exports/approve`,
    payload, config)).data;

/**
 * What this customer contacted us about before.
 *
 * Reached through a conversation the caller may already open, so it inherits
 * that permission. Each entry is a date, a channel, a purpose and a status -
 * never a subject, a message or a contact detail.
 */
export const getPriorContacts = async (conversationId, params = {}, config = {}) =>
  (await api.get(`/conversations/${conversationId}/prior-contacts`, { ...config, params })).data;

/** Set or correct why this contact happened. Only ever stated by a person. */
export const setContactPurpose = async (conversationId, purpose, config = {}) =>
  (await api.put(`/conversations/${conversationId}/contact-purpose`, { purpose }, config)).data;
